import { Link, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import { getCmsNewsImageUrlsSorted } from '../api/cmsNews';
import { USE_STATIC_SITE_CONTENT } from '../config/staticSiteMode';
import { getStaticNewsById } from '../data/staticSite/content';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { stripHtmlForMeta, truncateMetaDescription } from '../utils/metaText';

function formatNewsDate(isoDate) {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('tr-TR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

/**
 * Düz metin: paragraflar; HTML benzeri içerik: sınırlı güvenli render (CMS kaynaklı).
 * @param {string} raw
 */
function ArticleBody({ raw }) {
    if (!raw || !String(raw).trim()) {
        return null;
    }
    const t = String(raw).trim();
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(t);

    if (looksLikeHtml) {
        return (
            <div
                className="news-detail-body-html"
                dangerouslySetInnerHTML={{ __html: t }}
            />
        );
    }

    const blocks = t.split(/\n{2,}/);
    return (
        <div className="news-detail-body-text">
            {blocks.map((block, i) => (
                <p key={i} className="news-detail-body-p mb-0">
                    {block.split('\n').map((line, j) => (
                        <span key={j}>
                            {j > 0 ? <br /> : null}
                            {line}
                        </span>
                    ))}
                </p>
            ))}
        </div>
    );
}

/**
 * Haber detayı — URL: /news/:newsId
 * API: GET /api/cms/News/GetById?id=
 */
export function NewsDetailPage() {
    const { t } = useTranslation();
    const { newsId } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(!USE_STATIC_SITE_CONTENT);
    const [error, setError] = useState(null);

    const fancyboxGroup = useMemo(
        () => `news-detail-${newsId || 'gallery'}`,
        [newsId],
    );

    const { heroUrl, galleryUrls } = useMemo(() => {
        if (!article) {
            return { heroUrl: null, galleryUrls: [] };
        }
        const urls = getCmsNewsImageUrlsSorted(article);
        return {
            heroUrl: urls[0] || null,
            galleryUrls: urls.length > 1 ? urls.slice(1) : [],
        };
    }, [article]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!newsId) {
                setError('Geçersiz adres');
                setLoading(false);
                setArticle(null);
                return;
            }

            setLoading(true);
            setError(null);
            setArticle(null);

            if (USE_STATIC_SITE_CONTENT) {
                const data = getStaticNewsById(newsId);
                if (cancelled) return;
                if (!data) {
                    setError('Haber bulunamadı');
                    setArticle(null);
                } else {
                    setArticle(data);
                }
                setLoading(false);
                return;
            }

            /*
            try {
                const data = await fetchCmsNewsById(newsId);
                if (cancelled) return;
                setArticle(data);
            } catch (e) {
                if (cancelled) return;
                setError(
                    e instanceof Error ? e.message : 'Haber yüklenemedi',
                );
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
            */
        })();

        return () => {
            cancelled = true;
        };
    }, [newsId]);

    useEffect(() => {
        if (!heroUrl && galleryUrls.length === 0) return undefined;

        const escapedGroup = CSS.escape(fancyboxGroup);
        const selector = `[data-fancybox="${escapedGroup}"]`;

        Fancybox.bind(selector, {
            Carousel: {
                infinite: false,
            },
            compact: false,
            placeFocusBack: true,
        });

        return () => {
            Fancybox.close();
            Fancybox.unbind(selector);
        };
    }, [heroUrl, galleryUrls, fancyboxGroup]);

    if (loading) {
        return (
            <>
                <PageSeo
                    title={`${t('newsList.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.newsList')}
                />
                <SubpageLayout
                    title="Haberler"
                    subtitle="Yükleniyor…"
                    contentClassName="news-detail-page"
                >
                    <p
                        className="text-center text-secondary mb-0 py-5"
                        role="status"
                        aria-live="polite"
                    >
                        Haber yükleniyor…
                    </p>
                </SubpageLayout>
            </>
        );
    }

    if (error || !article) {
        return (
            <>
                <PageSeo
                    title={`${t('newsList.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.newsDetailFallback')}
                    noindex
                />
                <SubpageLayout
                    title="Haber bulunamadı"
                    subtitle={
                        error ||
                        'Aradığınız haber mevcut değil veya kaldırılmış olabilir.'
                    }
                    contentClassName="news-detail-page"
                >
                    <p className="news-detail-placeholder mb-4">
                        <Link
                            to="/news"
                            className="news-detail-back-link d-inline-flex align-items-center gap-2 text-decoration-none"
                        >
                            <ChevronLeft size={20} strokeWidth={2} aria-hidden />
                            Haberler listesine dön
                        </Link>
                    </p>
                </SubpageLayout>
            </>
        );
    }

    const dateLabel = formatNewsDate(article.createdAtUtc);
    const summary = (article.summary || '').trim();
    const hasBody = (article.content || '').trim();
    const metaDesc = truncateMetaDescription(
        stripHtmlForMeta((article.summary || article.content || '').trim()) ||
            t('seo.newsDetailFallback'),
    );

    return (
        <>
            <PageSeo
                title={`${article.title} — Haberler`}
                description={metaDesc}
                ogImage={heroUrl || undefined}
            />
            <SubpageLayout
                title={article.title}
                subtitle={dateLabel || undefined}
                contentClassName="news-detail-page"
            >
            <nav className="news-detail-nav mb-4" aria-label="İç sayfa">
                <Link
                    to="/news"
                    className="news-detail-back-link d-inline-flex align-items-center gap-2 text-decoration-none"
                >
                    <ChevronLeft size={20} strokeWidth={2} aria-hidden />
                    Haberlere dön
                </Link>
            </nav>

            {heroUrl ? (
                <div className="news-detail-hero rounded-4 mb-4">
                    <a
                        href={heroUrl}
                        className="news-detail-hero-link"
                        data-fancybox={fancyboxGroup}
                        data-caption={article.title}
                        aria-label={`${article.title} — büyük görsel`}
                    >
                        <img
                            src={heroUrl}
                            alt=""
                            className="news-detail-hero-img"
                        />
                    </a>
                </div>
            ) : null}

            {summary ? (
                <div className="news-detail-summary rounded-4 px-4 py-3 mb-4">
                    <p className="news-detail-summary-text mb-0">{summary}</p>
                </div>
            ) : null}

            {hasBody ? (
                <article className="news-detail-article mb-4">
                    <ArticleBody raw={article.content} />
                </article>
            ) : null}

            {!hasBody && !summary && !heroUrl ? (
                <p className="text-secondary mb-0">
                    Bu haber için ek metin bulunmuyor.
                </p>
            ) : null}

            {galleryUrls.length > 0 ? (
                <section className="news-detail-gallery" aria-label="Galeri">
                    <h2 className="news-detail-gallery-title h5 mb-3">
                        Fotoğraflar
                    </h2>
                    <ul className="news-detail-gallery-grid list-unstyled row row-cols-2 row-cols-lg-4 g-3 mb-0">
                        {galleryUrls.map((url) => (
                            <li key={url} className="col">
                                <a
                                    href={url}
                                    className="news-detail-gallery-link"
                                    data-fancybox={fancyboxGroup}
                                    data-caption={article.title}
                                >
                                    <img
                                        src={url}
                                        alt=""
                                        className="news-detail-gallery-thumb"
                                        loading="lazy"
                                    />
                                </a>
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}
        </SubpageLayout>
        </>
    );
}
